/* =============================================================
   jo-homepage.js
   Shared interactions across Joinery sections:
     1. Reveal-on-scroll (IntersectionObserver, one-shot)
     2. Number counter animation (eased)
     3. Hero image parallax (cheap GPU transform)
   Each feature is self-initialising on DOMContentLoaded and
   early-exits if its target elements aren't on the page.
   ============================================================= */
(function () {
  'use strict';

  // Respect prefers-reduced-motion; skip all non-essential animation.
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init() {
    initReveals();
    initCounters();
    initHeroParallax();
  }

  /* -----------------------------------------------------------
     1. Reveals — fade+slide elements with .jo-reveal into view.
        One-shot per element (unobserved after firing) so the
        animation doesn't retrigger when scrolling back up.
     ----------------------------------------------------------- */
  function initReveals() {
    var targets = document.querySelectorAll('.jo-reveal');
    if (!targets.length) return;

    // Fallback: show immediately if IntersectionObserver unsupported
    // (older browsers) so content isn't permanently invisible.
    if (!('IntersectionObserver' in window) || reduce) {
      targets.forEach(function (el) { el.classList.add('jo-reveal--in'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('jo-reveal--in');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -8% 0px' });

    targets.forEach(function (el) { io.observe(el); });
  }

  /* -----------------------------------------------------------
     2. Counters — animate a number from data-jo-from → data-jo-to
        once the element scrolls into view. Format with en-ZA
        locale so it groups as 1 000 000.
     ----------------------------------------------------------- */
  function initCounters() {
    var counters = document.querySelectorAll('[data-jo-counter]');
    if (!counters.length) return;

    counters.forEach(function (el) {
      var from = parseInt(el.getAttribute('data-jo-from'), 10) || 0;
      var to = parseInt(el.getAttribute('data-jo-to'), 10) || 0;
      var duration = parseInt(el.getAttribute('data-jo-duration'), 10) || 1800;
      var suffix = el.getAttribute('data-jo-suffix') || '';

      if (reduce || !('IntersectionObserver' in window)) {
        el.textContent = fmt(to) + suffix;
        return;
      }

      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            animate(el, from, to, duration, suffix);
            io.disconnect();
          }
        });
      }, { threshold: 0.4 });
      io.observe(el);
    });
  }

  function animate(el, from, to, duration, suffix) {
    var t0 = null;
    function step(t) {
      if (!t0) t0 = t;
      var p = Math.min((t - t0) / duration, 1);
      // easeOutCubic — quick start, soft landing on the target
      var eased = 1 - Math.pow(1 - p, 3);
      el.textContent = fmt(from + (to - from) * eased) + suffix;
      if (p < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  function fmt(n) {
    /* en-ZA groups with a plain space, which lets the number break
       across lines mid-value ("1 000" / "000+"). Swap the separators
       for non-breaking spaces so the figure always reads as one unit. */
    return Math.round(n).toLocaleString('en-ZA').replace(/\s/g, '\u00A0');
  }

  /* -----------------------------------------------------------
     3. Hero parallax — subtle translate on any image with
        data-jo-parallax. CSS background-attachment: fixed
        doesn't work on iOS so we use a throttled transform.
     ----------------------------------------------------------- */
  function initHeroParallax() {
    var targets = document.querySelectorAll('[data-jo-parallax]');
    if (!targets.length || reduce) return;

    var ticking = false;
    function update() {
      var y = window.scrollY;
      targets.forEach(function (el) {
        var speed = parseFloat(el.getAttribute('data-jo-parallax')) || 0.15;
        el.style.transform = 'translate3d(0,' + (y * speed) + 'px,0)';
      });
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();